import React, { useState } from 'react';
import { Drawer, Button, Typography, Divider, Space, Tag, Image, message, Tabs } from 'antd';
import { InputNumber } from 'antd';
import { MinusOutlined, PlusOutlined, DeleteOutlined, ShoppingCartOutlined, CalendarOutlined, SettingOutlined } from '@ant-design/icons';
import { useCart } from '../../contexts/CartContext';
import BookingModal from '../Booking/BookingModal';
import CateringCartDrawer from './CateringCartDrawer';

const { Title, Text } = Typography;

function CartDrawer() {
    const {
        cartItems,
        isCartOpen,
        closeCart,
        updateQuantity,
        removeFromCart,
        getCartTotal,
        getCartItemCount,
        clearCart,
        eventDetails
    } = useCart();
    
    const [showBookingModal, setShowBookingModal] = useState(false);
    const [activeTab, setActiveTab] = useState('items');

    const handleQuantityChange = (itemId, quantity, customizations) => {
        updateQuantity(itemId, quantity, customizations);
    };

    const handleRemoveItem = (itemId, customizations) => {
        removeFromCart(itemId, customizations);
        message.success('Item removed from cart');
    };

    const handleClearCart = () => {
        clearCart();
        message.success('Cart cleared');
    };

    // Use the new catering cart drawer if event details are present
    if (eventDetails.date || eventDetails.venue || eventDetails.guestCount > 0) {
        return <CateringCartDrawer />;
    }

    return (
        <>
        <Drawer
            title={
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#C97D60] flex items-center justify-center">
                            <ShoppingCartOutlined className="text-white text-lg" />
                        </div>
                        <div>
                            <span className="text-lg font-bold text-gray-900">Your Cart</span>
                            <div className="text-sm text-gray-500">{getCartItemCount()} {getCartItemCount() === 1 ? 'item' : 'items'}</div>
                        </div>
                    </div>
                    {cartItems.length > 0 && (
                        <Button 
                            type="link" 
                            danger 
                            onClick={handleClearCart}
                            size="small"
                            className="font-semibold"
                        >
                            Clear All
                        </Button>
                    )}
                </div>
            }
            placement="right"
            width={420}
            open={isCartOpen}
            onClose={closeCart}
            styles={{
                body: { padding: '16px' },
                header: { borderBottom: '1px solid rgba(229, 231, 235, 0.8)', padding: '20px' }
            }}
            footer={
                cartItems.length > 0 ? (
                    <div className="space-y-4 p-4 bg-gradient-to-br from-gray-50 to-white border-t border-gray-200">
                        <div className="flex justify-between items-center">
                            <span className="text-base font-semibold text-gray-700">Subtotal:</span>
                            <span className="price-modern text-xl font-bold">₹{getCartTotal().toLocaleString()}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Button 
                                onClick={closeCart}
                                className="h-12 rounded-lg font-semibold border border-gray-300 hover:border-[#C97D60] hover:text-[#C97D60] transition-all"
                            >
                                Continue Shopping
                            </Button>
                            <Button 
                                type="primary" 
                                className="btn-vibrant h-12 rounded-xl font-bold"
                                onClick={() => {
                                    closeCart();
                                    // If no event details, redirect to get started journey
                                    if (!eventDetails.date && !eventDetails.venue && eventDetails.guestCount === 0) {
                                        window.location.href = '/get-started';
                                    } else {
                                        window.location.href = '/checkout';
                                    }
                                }}
                            >
                                {(!eventDetails.date && !eventDetails.venue && eventDetails.guestCount === 0) 
                                    ? 'Get Started' 
                                    : 'Checkout'
                                }
                            </Button>
                        </div>
                    </div>
                ) : null
            }
        >
            {cartItems.length === 0 ? (
                <div className="text-center py-16">
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#F5F3F0] to-[#E8D5CC] flex items-center justify-center">
                        <ShoppingCartOutlined className="text-5xl text-[#C97D60]" />
                    </div>
                    <Title level={4} className="text-gray-700 font-bold mb-2">Your cart is empty</Title>
                    <Text type="secondary" className="text-base">Add some delicious items to get started!</Text>
                    <Button 
                        type="primary" 
                        className="btn-vibrant mt-6"
                        onClick={closeCart}
                    >
                        Start Shopping
                    </Button>
                </div>
            ) : (
                <div className="space-y-4">
                    {cartItems.map((item, index) => (
                        <div key={`${item.id}-${index}`} className="border border-gray-200 rounded-lg p-4 bg-white hover:shadow-md transition-all duration-300 modern-food-card">
                            <div className="flex gap-4">
                                <Image
                                    src={item.image}
                                    alt={item.name}
                                    width={90}
                                    height={90}
                                    className="rounded-xl object-cover shadow-md"
                                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqKg16B6oUSDAOKW1sVQK3O8CBJXJGYhhyB4BqLYoMDD1M2AwbJYxgYvh4iB0eQo8sSAxSgUo3w6B9pA4oaVfcZQBY9eJ+TAgxur+4UqgMDPtZGBgY/m//+f5sBgZ2DQ+H/9//v2f+//t/HwMDvQaGv5cA8cFj4hSXNnYAAABWZVhJZk1NACoAAAAIAAGHaQAEAAAAAQAAABoAAAAAAAOShgAHAAAAEgAAABgAAAAAAKACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAMAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCdABmX/9k="
                                />
                                <div className="flex-1">
                                    <Title level={5} className="mb-1 font-bold text-gray-900">{item.name}</Title>
                                    <Text type="secondary" className="text-sm font-medium">{item.vendor_name}</Text>
                                    <div className="mt-2">
                                        <Text strong className="price-modern text-lg font-bold">₹{parseFloat(item.price) || 0}</Text>
                                    </div>
                                    {Object.keys(item.customizations).length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-1">
                                            {Object.entries(item.customizations).map(([key, value]) => (
                                                <Tag key={key} size="small" className="mb-1 tag-modern">
                                                    {key}: {value}
                                                </Tag>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <Divider className="my-3 border-gray-200" />
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Button
                                        icon={<MinusOutlined />}
                                        size="small"
                                        onClick={() => handleQuantityChange(item.id, item.quantity - 1, item.customizations)}
                                        className="rounded-lg border border-gray-300 hover:border-indigo-400"
                                    />
                                    <InputNumber
                                        min={1}
                                        max={100}
                                        value={item.quantity}
                                        onChange={(value) => handleQuantityChange(item.id, value, item.customizations)}
                                        className="w-16 rounded-lg"
                                        size="small"
                                        style={{ borderRadius: '8px' }}
                                    />
                                    <Button
                                        icon={<PlusOutlined />}
                                        size="small"
                                        onClick={() => handleQuantityChange(item.id, item.quantity + 1, item.customizations)}
                                        className="rounded-lg border border-[#C97D60] bg-[#F5F3F0] hover:bg-[#E8D5CC] text-[#C97D60]"
                                    />
                                </div>
                                <div className="flex items-center gap-3">
                                    <Text strong className="price-modern text-lg font-bold">₹{((parseFloat(item.price) || 0) * (parseInt(item.quantity) || 0)).toLocaleString()}</Text>
                                    <Button
                                        icon={<DeleteOutlined />}
                                        size="small"
                                        danger
                                        onClick={() => handleRemoveItem(item.id, item.customizations)}
                                        className="rounded-lg"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </Drawer>
        
        {/* Booking Modal */}
        <BookingModal
            visible={showBookingModal}
            onCancel={() => setShowBookingModal(false)}
            onSuccess={() => {
                setShowBookingModal(false);
                closeCart();
            }}
        />
        </>
    );
}

export default CartDrawer;
